import { Header } from 'components/Header';
import { useLanguage } from 'features/hooks/useLanguage';
import { useProjectId } from 'hooks/store';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LanguageType } from 'types';
import { SelectFile } from './steps/select-file/SelectFile';
import { MapFields } from './steps/map-fields/MapFields';
import { Grid } from 'components/UI/Grid';
import { ConfirmFields } from './steps/ConfirmFields';
import { CodelistRecord, CodelistRecordKey } from '../domain/codelistRecordSchema';
import { ImportCompleted } from './steps/ImportCompleted';

export type ImportMode = 'full' | 'partial';

export const ImportCodelistPage = () => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	const params = useParams();
	const projectId = params.projectId as string;

	const [, setProjectId] = useProjectId();

	useEffect(() => {
		if (projectId) {
			setProjectId(projectId);
		}
	}, [projectId, setProjectId]);

	const [step, setStep] = useState<Steps>({ type: 'select-file', importMode: 'partial' });

	return (
		<>
			<Header.Main />
			<Header.Navigation />
			<Header.Title title={translation.title} />

			<Grid.Container>
				<div className="flex flex-col">
					{step.type === 'select-file' && (
						<SelectFile
							initialImportMode={step.importMode}
							onFileSelected={({ columns, rows, importMode }) => {
								setStep({ type: 'map-fields', columns, rows, importMode });
							}}
						/>
					)}

					{step.type === 'map-fields' && (
						<MapFields
							importedCodelistColumnNames={step.columns}
							importMode={step.importMode}
							onConfirm={mappedCodelist =>
								setStep({
									type: 'confirm-fields',
									mappedCodelist,
									columns: step.columns,
									rows: step.rows,
									importMode: step.importMode
								})
							}
							rows={step.rows}
							onBackClicked={() =>
								setStep({ type: 'select-file', importMode: step.importMode })
							}
						/>
					)}

					{step.type === 'confirm-fields' && (
						<ConfirmFields
							mappedCodelist={step.mappedCodelist}
							importMode={step.importMode}
							onBackClicked={() =>
								setStep({
									type: 'map-fields',
									columns: step.columns,
									rows: step.rows,
									importMode: step.importMode
								})
							}
							onImportCompleted={({ importedCount, skippedCount }) => {
								setStep({
									type: 'import-completed',
									importedCount,
									skippedCount
								});
							}}
							rows={step.rows}
						/>
					)}

					{step.type === 'import-completed' && (
						<ImportCompleted
							importedCount={step.importedCount}
							skippedCount={step.skippedCount}
						/>
					)}
				</div>
			</Grid.Container>
		</>
	);
};

type SelectFileStep = {
	type: 'select-file';
	importMode: ImportMode;
};

type MapFieldsStep = {
	type: 'map-fields';
	columns: string[];
	rows: ImportedRow[];
	importMode: ImportMode;
};

export type ImportedFileFieldNameByCodelistRecordFieldName = Omit<
	Record<CodelistRecordKey, string>,
	'customId'
>;
export type MappedCodelist = {
	entryIdentifyingVariable: {
		name: string;
		label: string;
	};
	codelistIdentifyingRowName: string;
	fieldMapping: ImportedFileFieldNameByCodelistRecordFieldName;
	parsedRecords: CodelistRecord[];
};
export type ImportedRow = Record<string, string>;

type ConfirmFieldsStep = {
	type: 'confirm-fields';
	mappedCodelist: MappedCodelist;
	columns: string[];
	rows: ImportedRow[];
	importMode: ImportMode;
};

type ImportCompletedStep = {
	type: 'import-completed';
	importedCount: number;
	skippedCount: number;
};

type Steps = SelectFileStep | MapFieldsStep | ConfirmFieldsStep | ImportCompletedStep;

const DICTIONARY = {
	[LanguageType.English]: {
		title: 'Import codelist'
	},
	[LanguageType.Norwegian]: {
		title: 'Importer kodeliste'
	}
} as const;
